/**
 * EntityActivityFeed — the shared activity timeline (D3, D4d).
 *
 * One component for all nine surfaces (Mieter, Mietverhältnis, Unit,
 * Bewerbung, Vermieter, Aufgabe, Rapport, Liegenschaft, Zimmer). The
 * only per-entity input is ``entityType`` and ``entityId``; there is no
 * behavioural branching and no scope map here, so the nine views cannot
 * drift apart.
 *
 * D4d added the two headline props. Not styling — honesty: property and
 * room feeds carry tasks and reports ONLY, so the default subtitle
 * ("… Notizen und relevante Änderungen") would promise two things they
 * never show. A property's field changes live in its own Historie card,
 * and a room has no change stream at all. The heading says which of the
 * two a reader is looking at; both default to what the seven existing
 * mounts already render, so none of them change.
 *
 * Read-only by construction: nothing here creates, edits or deletes.
 * Entries link out to the task and report pages that already exist and
 * invent no route for notes or changes.
 *
 * Semantics live in the backend — this renders what the feed returns and
 * never re-derives attribution client-side.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  Clock,
  MessageSquare,
  PencilLine,
} from "lucide-react";

import { formatDateTime } from "../../../utils/datetime";
import {
  ACTIVITY_FILTERS,
  useEntityActivityFeed,
} from "./useEntityActivityFeed";

/** Icon and accent per activity type. The icon is never the only
 * carrier of meaning — every entry also states its action in words. */
const TYPE_STYLE = {
  task: {
    Icon: ClipboardCheck,
    dot: "bg-sky-500",
    tint: "text-sky-600 dark:text-sky-400",
    noun: "Aufgabe",
  },
  work_report: {
    Icon: Clock,
    dot: "bg-amber-500",
    tint: "text-amber-600 dark:text-amber-400",
    noun: "Rapport",
  },
  note: {
    Icon: MessageSquare,
    dot: "bg-emerald-500",
    tint: "text-emerald-600 dark:text-emerald-400",
    noun: "Notiz",
  },
  change: {
    Icon: PencilLine,
    dot: "bg-muted-foreground",
    tint: "text-muted-foreground",
    noun: "Änderung",
  },
};

const FALLBACK_STYLE = TYPE_STYLE.change;

/** Where an entry opens, or null when it has no page of its own.
 * Only the two routes that actually exist — nothing is invented for a
 * note or a change. */
export function activityLinkPath(item) {
  if (!item?.link_id) return null;
  if (item.source_entity_type === "task") return `/admin/tasks/${item.link_id}`;
  if (item.source_entity_type === "work_report") {
    return `/admin/work-reports/${item.link_id}`;
  }
  return null;
}

/** "Heute, 15:10" / "Gestern, 09:20" / "05.08.2026, 14:52".
 *
 * Built on the app-wide `formatDateTime`, which already renders in
 * Europe/Zurich — so the relative label and the absolute one can never
 * disagree about which calendar day an entry belongs to. */
export function formatActivityTimestamp(iso, now = new Date()) {
  if (!iso) return "";
  const full = formatDateTime(iso);
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return full;
  // formatDateTime yields "DD.MM.YYYY, HH:MM" in Zurich time; reuse its
  // own halves rather than re-deriving them in another timezone.
  const [datePart, timePart] = full.split(", ");
  if (!timePart) return full;
  const today = formatDateTime(now.toISOString()).split(", ")[0];
  const yesterday = formatDateTime(
    new Date(now.getTime() - 86400000).toISOString()
  ).split(", ")[0];
  if (datePart === today) return `Heute, ${timePart}`;
  if (datePart === yesterday) return `Gestern, ${timePart}`;
  return full;
}

/** The small grey line under an entry: actor first, then whatever safe
 * scalars the backend attached. */
export function metaLine(item) {
  const extras = Object.values(item?.meta || {}).filter(
    (v) => typeof v === "string" && v.trim()
  );
  return [item?.actor_display, ...extras].filter(Boolean).join(" · ");
}

function FilterChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Aktivitäten filtern">
      {ACTIVITY_FILTERS.map((f) => {
        const active = f.key === value;
        return (
          <button
            key={f.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(f.key)}
            className={
              "rounded-full border px-3 py-1 text-[12px] font-medium transition-colors " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
              (active
                ? "border-transparent bg-primary text-primary-foreground"
                : "border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground dark:border-[#1e2130]")
            }
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

function ActivityRow({ item, isLast }) {
  const style = TYPE_STYLE[item.activity_type] || FALLBACK_STYLE;
  const { Icon } = style;
  const path = activityLinkPath(item);
  const headline = item.summary || item.title;

  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {/* The rail: one segment per entry except the last, so the line
          stops at the final dot instead of dangling. */}
      {!isLast ? (
        <span
          aria-hidden="true"
          className="absolute left-[13px] top-7 h-full w-px bg-border dark:bg-[#1e2130]"
        />
      ) : null}
      <span
        aria-hidden="true"
        className={
          "relative mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center " +
          "rounded-full border border-border bg-card dark:border-[#1e2130] dark:bg-[#13161f] " +
          style.tint
        }
      >
        <Icon className="h-3.5 w-3.5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[13px] font-semibold text-foreground dark:text-[#e2e4ed]">
            {item.title}
          </span>
          <time
            dateTime={item.occurred_at}
            className="shrink-0 text-[11px] tabular-nums text-muted-foreground dark:text-[#4a5070]"
          >
            {formatActivityTimestamp(item.occurred_at)}
          </time>
        </div>

        {item.summary ? (
          <p className="m-0 mt-0.5 break-words text-[13px] text-muted-foreground dark:text-[#94a3b8]">
            {path ? (
              <Link
                to={path}
                aria-label={`${style.noun} öffnen: ${headline}`}
                className="rounded-[4px] font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-[#c5cbe0]"
              >
                {item.summary}
              </Link>
            ) : (
              item.summary
            )}
          </p>
        ) : null}

        <p className="m-0 mt-0.5 text-[11px] text-muted-foreground dark:text-[#4a5070]">
          {metaLine(item)}
        </p>
      </div>
    </li>
  );
}

function Skeleton() {
  return (
    <ul aria-hidden="true" className="m-0 list-none space-y-4 p-0">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex gap-3">
          <span className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-muted dark:bg-[#1e2130]" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-2/5 animate-pulse rounded bg-muted dark:bg-[#1e2130]" />
            <div className="h-3 w-3/5 animate-pulse rounded bg-muted dark:bg-[#1e2130]" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function EntityActivityFeed({
  entityType,
  entityId,
  headingId = "entity-activity-heading",
  /** D4d: the section's visible name. Defaults to what the seven D3
   * surfaces already show; property and room pass "Operative Aktivität"
   * because a second, differently-scoped history sits beside them. */
  headingText = "Aktivität",
  /** D4d: the line under the heading. It must describe what this feed
   * actually contains — a default listing notes and changes on a page
   * that shows neither is a promise the feed cannot keep. */
  description = "Aufgaben, Rapporte, Notizen und relevante Änderungen",
  /** REPORT-UX1: bump to refetch after a sibling section mutated (a
   * note, an attachment, a cancellation) — the feed must surface the
   * new event without a page reload. */
  refreshKey = 0,
}) {
  const feed = useEntityActivityFeed({ entityType, entityId, refreshKey });
  const {
    items,
    total,
    hasMore,
    filter,
    setFilter,
    loading,
    loadingMore,
    error,
    loadMoreError,
    retry,
    loadMore,
  } = feed;

  const isEmpty = !loading && !error && items.length === 0;

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#13161f]"
    >
      <header className="mb-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2
            id={headingId}
            className="m-0 text-[12px] font-semibold uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]"
          >
            {headingText}
          </h2>
          {total > 0 ? (
            <span className="text-[11px] text-muted-foreground dark:text-[#4a5070]">
              {items.length} von {total}
            </span>
          ) : null}
        </div>
        <p className="m-0 mt-1 text-[11px] text-muted-foreground dark:text-[#4a5070]">
          {description}
        </p>
      </header>

      <div className="mb-3">
        <FilterChips value={filter} onChange={setFilter} />
      </div>

      {error ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-[8px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={retry}
            className="rounded-[6px] border border-destructive/40 bg-transparent px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {loading && items.length === 0 ? (
        <>
          <p role="status" className="sr-only">
            Aktivitäten werden geladen …
          </p>
          <Skeleton />
        </>
      ) : null}

      {isEmpty ? (
        <p
          role="status"
          className="m-0 rounded-[8px] border border-dashed border-border px-3 py-6 text-center text-[13px] text-muted-foreground dark:border-[#1e2130] dark:text-[#4a5070]"
        >
          {filter === "all"
            ? "Für dieses Objekt sind noch keine Aktivitäten vorhanden."
            : "Für diesen Filter wurden keine Aktivitäten gefunden."}
        </p>
      ) : null}

      {items.length > 0 ? (
        <ol className="relative m-0 list-none p-0">
          {items.map((item, index) => (
            <ActivityRow
              key={item.id}
              item={item}
              isLast={index === items.length - 1}
            />
          ))}
        </ol>
      ) : null}

      {loadMoreError ? (
        <div
          role="alert"
          className="mt-2 flex flex-wrap items-center gap-2 rounded-[8px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive"
        >
          <span>{loadMoreError}</span>
          <button
            type="button"
            onClick={loadMore}
            className="rounded-[6px] border border-destructive/40 bg-transparent px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {hasMore ? (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#c5cbe0]"
          >
            {loadingMore ? "Lädt …" : "Mehr laden"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
